import React, { useRef, useState } from 'react';
import { ActivityIndicator, Image, Modal, NativeModules, Platform, Pressable, Share, StyleSheet, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import QRCode from 'react-native-qrcode-svg';
import { SvgXml } from 'react-native-svg';
import { captureRef } from 'react-native-view-shot';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { useI18n } from '../i18n';
import type { VenueSummary } from '../types';
import { buildTonyteVenueLink } from '../utils/shareLinks';
import { shareVenueProfile } from '../hooks/useVenueVibes';

import { TONYTE_LOGO_ASPECT, TONYTE_LOGO_SVG } from '../assets/tonyteLogoSvg';

const MAX_CARD_WIDTH = 300;
const MAX_QR_SIZE = 208;
const CARD_HORIZONTAL_PADDING = 22;
// Center badge ≈ 22% of the code's width: well inside what error correction
// level H recovers, so every phone camera still reads it.
const BADGE_SIZE = 46;
const BADGE_LOGO_HEIGHT = 30;

// A partner's profile QR code as a branded card. "Share QR code" renders the
// card to a PNG (react-native-view-shot) and opens the system share sheet with
// the image, so partners can post it to Instagram, WhatsApp, print it, etc.
// The card is deliberately brand-styled (not theme-dependent): it should look
// the same wherever it ends up.
const PartnerQrSheet: React.FC<{ venue: VenueSummary | null; visible: boolean; onClose: () => void }> = ({ venue, visible, onClose }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();
  const { width: windowWidth } = useWindowDimensions();
  const cardRef = useRef<View>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!venue) return null;
  const link = buildTonyteVenueLink(venue.id);
  const canShareImage = Platform.OS !== 'web';
  const cardWidth = Math.min(MAX_CARD_WIDTH, windowWidth - 32);
  const qrSize = Math.min(MAX_QR_SIZE, cardWidth - (CARD_HORIZONTAL_PADDING * 2) - 24);

  const shareImage = async () => {
    setBusy(true);
    setError(null);
    try {
      const uri = await captureRef(cardRef, { format: 'png', quality: 1, result: 'tmpfile' });
      const fileUri = uri.startsWith('file://') ? uri : `file://${uri}`;
      if (Platform.OS === 'android') {
        const native = NativeModules.TonyteShareImage;
        if (!native?.shareImage) throw new Error('Image sharing is not available in this version.');
        await native.shareImage(fileUri, venue.name);
      } else {
        await Share.share({ url: fileUri, title: venue.name });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const sheetColors = {
    backgroundColor: theme.isDark ? '#111826' : theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.root}>
        <Pressable style={[StyleSheet.absoluteFill, styles.backdrop]} onPress={onClose} accessibilityLabel={t('common.done')} />
        <View style={[styles.sheet, sheetColors, { paddingBottom: insets.bottom + 18 }]}>
          <View style={[styles.grabber, { backgroundColor: theme.colors.border }]} />

          {/* Captured as the shared image — keep everything visual inside. */}
          <View ref={cardRef} collapsable={false} style={[styles.cardShadow, { width: cardWidth }]}>
            <LinearGradient colors={['#1A1033', '#0B1220', '#2A1406']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={[styles.card, { width: cardWidth }]}>
              <View style={styles.glow} />
              <View style={styles.brandRow}>
                <SvgXml xml={TONYTE_LOGO_SVG} width={20 * TONYTE_LOGO_ASPECT} height={20} />
                <Text style={styles.brandText} maxFontSizeMultiplier={1.2}>TONYTE</Text>
              </View>
              <View style={styles.identity}>
                {venue.logoUrl ? (
                  <Image source={{ uri: venue.logoUrl }} style={styles.avatar} />
                ) : (
                  <View style={[styles.avatar, styles.avatarFallback]}><Text style={styles.avatarLetter} maxFontSizeMultiplier={1.2}>{venue.name.slice(0, 1).toUpperCase()}</Text></View>
                )}
                <View style={styles.identityCopy}>
                  <Text style={styles.name} numberOfLines={2} adjustsFontSizeToFit minimumFontScale={0.82} maxFontSizeMultiplier={1.2}>{venue.name}</Text>
                  <Text style={styles.handle} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.82} maxFontSizeMultiplier={1.2}>{[venue.username ? `@${venue.username}` : '', venue.city].filter(Boolean).join(' · ')}</Text>
                </View>
              </View>
              <View style={styles.qrTile}>
                <QRCode value={link} size={qrSize} color="#0B1220" backgroundColor="#FFFFFF" ecl="H" quietZone={6} />
                {/* Drawn over the code (not the library's square `logo` slot, which
                    crops the non-square pin) as a vector, so it stays crisp. */}
                <View pointerEvents="none" style={styles.badgeWrap}>
                  <View style={styles.badge}>
                    <SvgXml xml={TONYTE_LOGO_SVG} width={BADGE_LOGO_HEIGHT * TONYTE_LOGO_ASPECT} height={BADGE_LOGO_HEIGHT} />
                  </View>
                </View>
              </View>
              <Text style={styles.cta} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.72} maxFontSizeMultiplier={1.2}>{t('partnerQr.scan')}</Text>
              <Text style={styles.link} numberOfLines={1} maxFontSizeMultiplier={1.2}>{link.replace(/^https?:\/\//, '')}</Text>
            </LinearGradient>
          </View>

          {error ? <Text style={styles.error}>{error}</Text> : null}
          <View style={styles.actions}>
            {canShareImage ? (
              <TouchableOpacity style={[styles.action, styles.actionPrimary]} onPress={shareImage} disabled={busy} accessibilityRole="button">
                {busy ? <ActivityIndicator color="#fff" /> : <Icon name="image" size={16} color="#fff" />}
                <Text style={[styles.actionText, styles.actionPrimaryText]}>{t('partnerQr.shareImage')}</Text>
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={[styles.action, { backgroundColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
              onPress={() => void shareVenueProfile(venue)}
              accessibilityRole="button"
            >
              <Icon name="link" size={16} color={theme.colors.icon} />
              <Text style={[styles.actionText, { color: theme.colors.textPrimary }]}>{t('partnerQr.shareLink')}</Text>
            </TouchableOpacity>
          </View>
          <Text style={[styles.hint, { color: theme.colors.textMuted }]}>{canShareImage ? t('partnerQr.hint') : t('partnerQr.hintWeb')}</Text>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, justifyContent: 'flex-end' },
  backdrop: { backgroundColor: 'rgba(0,0,0,0.55)' },
  sheet: { borderTopLeftRadius: 26, borderTopRightRadius: 26, borderWidth: 1, paddingTop: 8, paddingHorizontal: 16, alignItems: 'center', gap: 14 },
  grabber: { width: 40, height: 4, borderRadius: 2, marginBottom: 4 },
  cardShadow: { borderRadius: 28, shadowColor: '#F27D26', shadowOpacity: 0.25, shadowRadius: 24, shadowOffset: { width: 0, height: 10 }, elevation: 8 },
  card: { borderRadius: 28, paddingHorizontal: CARD_HORIZONTAL_PADDING, paddingTop: 20, paddingBottom: 22, alignItems: 'center', overflow: 'hidden' },
  glow: { position: 'absolute', top: -80, right: -60, width: 220, height: 220, borderRadius: 110, backgroundColor: 'rgba(242,125,38,0.22)' },
  brandRow: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start' },
  brandText: { color: '#FF9A3C', fontSize: 12, fontWeight: Typography.extrabold, letterSpacing: 3 },
  identity: { flexDirection: 'row', alignItems: 'center', gap: 12, alignSelf: 'stretch', marginTop: 16, marginBottom: 18 },
  avatar: { width: 46, height: 46, borderRadius: 23, borderWidth: 2, borderColor: 'rgba(255,255,255,0.85)' },
  avatarFallback: { backgroundColor: '#A855F7', alignItems: 'center', justifyContent: 'center' },
  avatarLetter: { color: '#fff', fontSize: 20, fontWeight: Typography.extrabold },
  identityCopy: { flex: 1, minWidth: 0 },
  name: { color: '#fff', fontSize: 19, lineHeight: 23, fontWeight: Typography.extrabold },
  handle: { color: 'rgba(255,255,255,0.62)', fontSize: 13, marginTop: 2 },
  qrTile: { backgroundColor: '#fff', borderRadius: 22, padding: 12 },
  badgeWrap: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center' },
  badge: {
    width: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: 13,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: 'rgba(242,125,38,0.22)',
  },
  cta: { alignSelf: 'stretch', color: '#fff', fontSize: 15, fontWeight: Typography.extrabold, marginTop: 16, textAlign: 'center' },
  link: { color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 4 },
  error: { color: '#EF4444', fontSize: 13, textAlign: 'center' },
  actions: { flexDirection: 'row', gap: 10, alignSelf: 'stretch' },
  action: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 16, paddingVertical: 14 },
  actionPrimary: { backgroundColor: '#F27D26' },
  actionText: { fontSize: 14, fontWeight: Typography.extrabold },
  actionPrimaryText: { color: '#fff' },
  hint: { fontSize: 12, textAlign: 'center', lineHeight: 17 },
});

export default PartnerQrSheet;
